import { areAllChildrenPredicted } from "#modules/quoteRequestLines/helpers/areAllChildrenPredicted";
import { getAreAllAssemblyChildrenAreQuoted } from "#modules/quoteRequestLines/helpers/getAreAllAssemblyChildrenAreClustered";
import { getIsAnalyzingAssembly } from "#modules/quoteRequestLines/helpers/getIsAnalyazingAssembly";
import { getIsAnalyzingPart } from "#modules/quoteRequestLines/helpers/getIsAnalyzingPart";
import { hasFeaturesDetectionError } from "#modules/quoteRequestLines/helpers/hasFeaturesDetectionError";
import {
  hasPredictionErrorForOneChild,
  hasPredictionMissingForOneChild,
} from "#modules/quoteRequestLines/helpers/hasPredictionErrorForOneChild";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  AssemblyWithChildren,
  checkPartDimensions,
  getFirst2DFile,
  hasInvalidInputError,
  hasValidDataToPredict,
  JobErrorCodeSchema,
  JobStatusSchema,
  MaterialGradeType,
  PartTypeSchema,
  PredictionModelTypeType,
  isJobProcessing,
} from "shared";

export type QuoteRequestLineState =
  | "consumable"
  | "analyzingPart"
  | "analyzingAssembly"
  | "analysisError"
  | "waitingForData"
  | "childrenPredictionError"
  | "childrenPredictionMissing"
  | "nesting"
  | "predicting"
  | "predictionInputError"
  | "partVolumeOutOfBounds"
  | "predictionError"
  | "predicted";

const PROCESSING_STATES: ReadonlySet<QuoteRequestLineState> = new Set([
  "analyzingPart",
  "analyzingAssembly",
  "nesting",
  "predicting",
]);

export const isQuoteRequestLineStateProcessing = (state: QuoteRequestLineState): boolean =>
  PROCESSING_STATES.has(state);

const isNestingProcessing = (line: QuoteRequestLineDto): boolean =>
  !!line.nestingStatus && isJobProcessing(line.nestingStatus);

const isAssemblySubtreeNesting = (assembly: AssemblyWithChildren<QuoteRequestLineDto>): boolean =>
  isNestingProcessing(assembly.quoteRequestLine) ||
  assembly.children.some(isAssemblySubtreeNesting);

export type GetQuoteRequestLineStateParams = {
  quoteRequestLine: QuoteRequestLineDto;
  part: QuoteRequestLineDto["part"];
  materialGrade: MaterialGradeType | null;
  isChild: boolean;
  hasChildren: boolean;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  currentAssemblyChildren: AssemblyWithChildren<QuoteRequestLineDto>[];
  isClustering: boolean;
  modelType: PredictionModelTypeType | null;
};

export const getQuoteRequestLineState = ({
  quoteRequestLine: line,
  part,
  materialGrade,
  isChild,
  hasChildren,
  assembliesInBatch,
  currentAssemblyChildren,
  isClustering,
  modelType,
}: GetQuoteRequestLineStateParams): QuoteRequestLineState => {
  if (part.type === PartTypeSchema.enum.CONSUMABLE) {
    return "consumable";
  }

  const hasClusterParts = (line.cluster?.targetQuoteRequestLines.length ?? 0) > 0;

  if (getIsAnalyzingPart(part) && !hasClusterParts) {
    return "analyzingPart";
  }

  if (getIsAnalyzingAssembly(part)) {
    return "analyzingAssembly";
  }

  const has3DAnalysisSucceeded = part.analyzeStatus === JobStatusSchema.enum.PROCESSED;
  const has2DAnalysisSucceeded = part.analyze2DStatus === JobStatusSchema.enum.PROCESSED;
  const has2DAnalysisFailed = part.analyze2DStatus === JobStatusSchema.enum.ERROR;

  if (
    (hasFeaturesDetectionError(modelType, part) && !has2DAnalysisSucceeded) ||
    (has2DAnalysisFailed && !has3DAnalysisSucceeded)
  ) {
    return "analysisError";
  }

  const hasPdfFile = !!getFirst2DFile(part.files);
  if (hasPdfFile && isJobProcessing(part.analyze2DStatus)) {
    return "analyzingPart";
  }

  const areAllAssemblyChildrenAreQuoted = getAreAllAssemblyChildrenAreQuoted({
    quoteRequestLine: line,
    children: currentAssemblyChildren,
  });
  const isQuoteRequestLinePredicted = line.predictionStatus === JobStatusSchema.enum.PROCESSED;
  const hasSourceData = hasValidDataToPredict({
    materialGrade,
    isAssembly: part.type === PartTypeSchema.enum.ASSEMBLY,
    children: assembliesInBatch.map((assembly) => assembly.quoteRequestLine),
  });

  if (
    !isQuoteRequestLinePredicted &&
    ((!isClustering && !hasSourceData) ||
      (isClustering &&
        ((!hasChildren && !hasClusterParts) || (hasChildren && !areAllAssemblyChildrenAreQuoted))))
  ) {
    return "waitingForData";
  }

  if (hasPredictionErrorForOneChild(currentAssemblyChildren)) {
    return "childrenPredictionError";
  }

  if (hasPredictionMissingForOneChild(currentAssemblyChildren)) {
    return "childrenPredictionMissing";
  }

  const isNesting =
    isNestingProcessing(line) || currentAssemblyChildren.some(isAssemblySubtreeNesting);
  if (isNesting) {
    return "nesting";
  }

  const isPredictingParent = !isChild && !areAllChildrenPredicted(currentAssemblyChildren);
  if ((!!line.predictionStatus && isJobProcessing(line.predictionStatus)) || isPredictingParent) {
    return "predicting";
  }

  const partDimensionCheck = checkPartDimensions(part);
  const isPredictionInputError =
    line.latestPredictionJob?.errorCode === JobErrorCodeSchema.enum.INVALID_INPUT &&
    hasInvalidInputError(line.latestPredictionJob.errorMetadata);

  if (
    isPredictionInputError ||
    (partDimensionCheck.hasError && partDimensionCheck.error === "missing_dimension")
  ) {
    return "predictionInputError";
  }

  if (partDimensionCheck.hasError && partDimensionCheck.error === "volume_out_of_bounds") {
    return "partVolumeOutOfBounds";
  }

  if (line.predictionStatus === JobStatusSchema.enum.ERROR) {
    return "predictionError";
  }

  return "predicted";
};
